import {
  AreaState,
  SubRegionAction,
  assignEdits,
  subRegionEdits,
} from '../rules/suburb-subregion-edits';
import { displayName } from './map-geometry';
import { Gesture, MapEdit, MapMode, MapPage, MapSubRegion } from './map-types';

/**
 * What a map gesture does in each mode (#256, #260), as pure functions: the edits, and what the status line says
 * about the suburbs it left alone. The engine applies the edits locally and reports them for saving; the server has
 * the last word (conductor-server suburb-area-plan.ts planEdits), and these mirror it. Tested in map-editing.spec.ts.
 */

/** The modes that edit. */
export type EditMode = Exclude<MapMode, 'view'>;

export interface EditContext {
  mode: EditMode;
  areas: ReadonlyMap<string, AreaState>;
  pages: ReadonlyMap<string, MapPage>;
  activeRegion: string | null;
  /** Assign mode: the sub-region picked for the active region (or null). Sub-region mode: the one being edited. */
  subRegion: MapSubRegion | null;
  /** Sub-region mode: whether a sweep adds or takes out. */
  subRegionAction: SubRegionAction;
  regionName(id: string): string;
  /** A suburb's ABS name, '' when it isn't drawn. */
  areaName(code: string): string;
}

export interface StatusLine {
  message: string;
  error: boolean;
}

export interface EditPlan {
  edits: MapEdit[];
  /** What to tell the person (why something was left alone), or null. */
  status: StatusLine | null;
}

const ERROR = (message: string): StatusLine => ({ message, error: true });
/** How many names a status line lists before it trails off. */
const NAMES_SHOWN = 5;
const plural = (n: number, one = '', many = 's') => (n === 1 ? one : many);

/**
 * The edits these suburbs get in the context's mode. Assign: into the active region (and its picked sub-region).
 * Erase: out of their region. Cover: a click toggles cover, a sweep only covers. Sub-region: membership of one
 * sub-region (suburb-subregion-edits.ts). Suburbs with a page are left alone where the server would refuse.
 */
export function planEdits(codes: string[], gesture: Gesture, ctx: EditContext): EditPlan {
  if (ctx.mode === 'subregion') return subRegionPlan(codes, gesture, ctx);
  if (ctx.mode === 'erase')
    return withKept(
      regionModeEdits(codes, gesture, 'erase', ctx.activeRegion, ctx.areas, ctx.pages)
    );
  const active = ctx.activeRegion;
  if (!active) return { edits: [], status: ERROR('Pick a region first.') };
  if (ctx.mode === 'assign')
    return withKept(assignEdits(codes, ctx.areas, active, ctx.subRegion, ctx.pages));
  return withKept(regionModeEdits(codes, gesture, 'cover', active, ctx.areas, ctx.pages));
}

/** A region mode's edits, and the pages it left alone named in the status. */
function withKept(plan: { edits: MapEdit[]; kept: string[] }): EditPlan {
  return { edits: plan.edits, status: plan.kept.length ? ERROR(keptPagesStatus(plan.kept)) : null };
}

/** Erase and Cover, suburb by suburb, against the map as it is before any of them applies. */
export function regionModeEdits(
  codes: string[],
  gesture: Gesture,
  mode: 'erase' | 'cover',
  active: string | null,
  areas: ReadonlyMap<string, AreaState>,
  pages: ReadonlyMap<string, MapPage>
): { edits: MapEdit[]; kept: string[] } {
  const edits: MapEdit[] = [];
  const kept: string[] = [];
  for (const code of codes) {
    const row = areas.get(code);
    const page = pages.get(code);
    if (mode === 'erase') {
      if (!row) continue;
      if (page) kept.push(page.label);
      else edits.push({ salCode: code, field: 'region', from: row.regionId, to: null });
    } else if (!row) {
      if (page && page.regionId !== active) kept.push(page.label);
      else
        edits.push(
          { salCode: code, field: 'region', from: null, to: active },
          { salCode: code, field: 'covered', from: '0', to: '1' }
        );
    } else if (!row.covered) {
      edits.push({ salCode: code, field: 'covered', from: '0', to: '1' });
    } else if (gesture === 'click') {
      if (page) kept.push(page.label);
      else edits.push({ salCode: code, field: 'covered', from: '1', to: '0' });
    }
  }
  return { edits, kept };
}

function subRegionPlan(codes: string[], gesture: Gesture, ctx: EditContext): EditPlan {
  const sr = ctx.subRegion;
  if (!sr) return { edits: [], status: ERROR('Pick a sub-region first.') };
  const plan = subRegionEdits(codes, ctx.areas, sr, ctx.subRegionAction, gesture, ctx.pages);
  if (plan.otherRegion.length) {
    const [first] = plan.otherRegion;
    const name = displayName(ctx.areaName(first.code)) || first.code;
    const more =
      plan.otherRegion.length > 1
        ? ` (and ${plan.otherRegion.length - 1} more in other regions)`
        : '';
    const home = ctx.regionName(sr.regionId);
    return {
      edits: plan.edits,
      status: ERROR(
        `${name} is in ${ctx.regionName(first.regionId)}; ${sr.name} is part of ${home}. Move it to ${home} first${more}.`
      ),
    };
  }
  if (plan.pages.length) {
    const n = plan.pages.length;
    const whose = n === 1 ? 'its page is' : 'their pages are';
    return {
      edits: plan.edits,
      status: ERROR(
        `Left ${n} suburb${plural(n)} alone: ${whose} in another region (${plan.pages.slice(0, NAMES_SHOWN).join(', ')}). Move the page first.`
      ),
    };
  }
  return { edits: plan.edits, status: null };
}

/** Suburbs a region mode left alone because of their page, named. */
export function keptPagesStatus(kept: string[]): string {
  const n = kept.length;
  const trail = n > NAMES_SHOWN ? '…' : '';
  return `Left ${n} suburb${plural(n)} alone: ${n === 1 ? 'it has' : 'they have'} a page (${kept.slice(0, NAMES_SHOWN).join(', ')}${trail}). Move the page first.`;
}

/** Why nothing on this map can be edited. */
export function readOnlyStatus(overview: boolean): string {
  return overview
    ? 'The overview is read-only: pick a state to edit.'
    : 'The suburb map is read-only.';
}

/** After a lasso: what it did, to how many. `alsoInto` names Assign mode's picked sub-region. */
export function lassoStatus(
  mode: MapMode,
  action: SubRegionAction,
  n: number,
  alsoInto: string | null
): string {
  const also = alsoInto ? ` (into ${alsoInto} too)` : '';
  return `${lassoVerb(mode, action)} ${n.toLocaleString()} suburb${plural(n)}${also} by lasso. Cmd+Z undoes all of it.`;
}

function lassoVerb(mode: MapMode, action: SubRegionAction): string {
  if (mode === 'erase') return 'Cleared';
  if (mode === 'cover') return 'Covered';
  if (mode === 'subregion') return action === 'remove' ? 'Took out' : 'Added';
  return 'Assigned';
}

/** After Grow: how far the region (and, in Assign, its picked sub-region) or the sub-region grew. */
export function grewStatus(name: string, n: number, alsoInto: string | null): string {
  return `Grew ${name} by ${n} suburb${plural(n)}${alsoInto ? `, all into ${alsoInto} too` : ''}. Cmd+Z undoes.`;
}
